import axios from "axios";

declare global {
    var appConfig: any;
}

export interface OrdererDeliveryRideInfo {
    rideId: string;
    carInfo?: CarInfo;
    driver?: DriverCommonInfo;
    driverPosition?: Location;
    route?: string;
    stops: DeliveryStop[];
    ordererName?: string;
    rating?: number;
    ratingIndications?: RatingIndications;
    tipCurrency?: string;
}

export interface DeliveryStop {
    stopId: string;
    destinationLocation: Location;
    details?: DeliveryStopDetails;
}

export interface DeliveryStopDetails {
    status: StopStatus;
    eta?: string;
    driverPhoneInfo?: DriverPhoneInfo;
    packageInfo: PackageInfo;
    verificationCode?: string;
    cancellable: boolean;
    cancelReasons?: StopCancelReasonsInfo[];
    cancelType?: "ORDERER" | "DRIVER" | "PASSENGER" | "RECIPIENT" | "SUPPLIER" | "LIFTAGO";
    messageToDriver?: boolean;
    messageToDriverEnabled: boolean;
    kind: StopKind;
    pudo?: PudoStorage;
}

export interface PudoStorage {
    pudoName?: string;
    pudoType: "LOCKER" | "PARCEL_SHOP";
    location: Location;
    images?: string[];
}

export interface DriverPhoneInfo {
    phoneNumber?: string;
    maskedNumber: boolean;
    ivrEnabled?: boolean;
    codeForCommunication?: string;
}

export interface Location {
    placeName?: string;
    lat: number;
    lon: number;
    bearing?: number;
}
export interface CarInfo {
    color: string;
    brand: string;
    model: string;
    licencePlate: string;
    carType: string;
    yearOfManufacture: number;
}

export type StopStatus =
    | "IN_QUEUE"
    | "DELIVERING"
    | "ARRIVED"
    | "DELIVERED"
    | "UNDELIVERED"
    | "CANCELLED"
    | "REDIRECTED"
    | "DELIVERED_TO_PUDO";

export type StopKind = "PICKUP" | "DROP_OFF" | "RETURN" | "PUDO";

export interface OtherDropOffLocation {
    location: Location;
    finished: boolean;
}

export interface RecipientStopInfo {
    kind: StopKind;
    rideId: string;
    pickupLocation?: Location;
    destinationLocation: Location;
    currentLocation?: Location;
    status: StopStatus;
    otherDropOffLocations: OtherDropOffLocation[];
    carInfo?: CarInfo;
    eta?: string;
    route?: string;
    packageInfo: PackageInfo;
    driver?: DriverInfo;
    verificationCode?: string;
    codeForCommunication?: string;
    redirectToPudoEnabled: boolean;
    depotExpiresAt?: string;
    isDepotFallback?: boolean;
    cancellable: boolean;
    cancelReasons?: StopCancelReasonsInfo[];
    messageToDriver?: string;
    messageToDriverEnabled: boolean;
    rating?: number;
    ratingIndications?: RatingIndications;
    businessCustomizationHtml?: string;
    pudo?: PudoStorage;
    ordererName?: string;
    tipCurrency?: string;
}

export interface StopCancelReasonsInfo {
    title: string;
    reason: string;
}

export interface PackageInfo {
    senderInfo?: PackageSenderInfo;
    recipientInfo?: PackageRecipientInfo;
    itemCount?: number;
    collectOnDelivery?: PackageCollectOnDelivery;
}

export interface PackageSenderInfo {
    name: string;
    businessId?: string;
    userId?: string;
}

export interface PackageRecipientInfo {
    name: string;
}

export interface PackageCollectOnDelivery {
    price: Money;
}

export interface Money {
    amount: number;
    currency: string;
}

export interface DriverCommonInfo {
    id: string;
    name: string;
    photo?: string;
    rating?: number;
}

export interface DriverInfo extends DriverCommonInfo, DriverPhoneInfo {}

export interface RecipientTrackingPageParams {
    shareKey: string;
}

export interface OrdererTrackingPageParams {
    shareKey: string;
}

export interface RatingRequest {
    comment?: string;
    negativeIndications?: string[];
    positiveIndications?: string[];
    rating: number;
}

export interface RatingIndications {
    negative: RatingIndication[];
    positive: RatingIndication[];
}

export interface RatingIndication {
    key: string;
    title: string;
}

export async function getOrdererRideInfo(shareKey: string): Promise<OrdererDeliveryRideInfo> {
    const response = await axios.get(
        `${appConfig.baseApiUrl}/tracking/delivery-stops/${encodeURIComponent(shareKey)}/multi`
    );
    return response.data;
}

export async function getRecipientStopInfo(shareKey: string): Promise<RecipientStopInfo> {
    const response = await axios.get(
        `${appConfig.baseApiUrl}/tracking/delivery-stops/${encodeURIComponent(shareKey)}`
    );
    return response.data;
}

export async function rateDelivery(shareKey: string, rating: RatingRequest) {
    const response = await axios.post(
        `${appConfig.baseApiUrl}/tracking/delivery-stops/${encodeURIComponent(shareKey)}/rate`,
        rating
    );
    return response;
}

export async function cancelStop(shareKey: string, cancelReason: string) {
    const response = await axios.post(
        `${appConfig.baseApiUrl}/tracking/delivery-stops/${encodeURIComponent(shareKey)}/cancel`,
        {reason: cancelReason}
    );
    return response.data;
}

export async function messageToDriver(shareKey: string, message: string) {
    const response = await axios.put(
        `${appConfig.baseApiUrl}/tracking/delivery-stops/${encodeURIComponent(
            shareKey
        )}/messageToDriver`,
        {message: message}
    );
    return response.data;
}
